import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import {
    Box,
    TextField,
    FormControl,
    Grid,
    Button,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Tooltip
} from "@mui/material";
import { UpdateFoodAllowanceAction } from "../../../../../../store/Finance/PayRollTemplate/Action";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const FoodAllowance = ({ editedRecord, setOpenPopup }) => {
    const dispatch = useDispatch();
    const initialValue = {
        food_allowance_type: "",
        flat_amount: "",
        min_food_allowance_days: "",
        variable_food_allowance_daily_rate: "",
        variable_food_allowance_max_rate: "",
    };
    const [foodAllowance, setFoodAllowance] = useState(initialValue);
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (editedRecord != null) {
            setFoodAllowance({
                ...editedRecord,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedRecord]);
    const validate = (fieldValues = foodAllowance) => {
        let temp = { ...errors };
        if ("food_allowance_type" in fieldValues)
            temp.food_allowance_type = fieldValues.food_allowance_type ? "" : "Food Allowance type is required.";

        if ("flat_amount" in fieldValues && foodAllowance.food_allowance_type === "Flat")
            temp.flat_amount = fieldValues.flat_amount ? "" : "Flat amount is required.";

        if (foodAllowance.food_allowance_type === "Variable") {
            if ("min_food_allowance_days" in fieldValues)
                temp.min_food_allowance_days = fieldValues.min_food_allowance_days ? "" : "Min. eligible days is required.";
            if ("variable_food_allowance_daily_rate" in fieldValues)
                temp.variable_food_allowance_daily_rate = fieldValues.variable_food_allowance_daily_rate ? "" : "Per-day rate is required.";
            if ("variable_food_allowance_max_rate" in fieldValues)
                temp.variable_food_allowance_max_rate = fieldValues.variable_food_allowance_max_rate ? "" : "Max. monthly limit is required.";
        }

        setErrors({ ...temp });

        return Object.values(temp).every((x) => x === "");
    };
    const handleInput = (event) => {
        const { name, value } = event.target;
        if (name === "food_allowance_type") {
            if (value === "Flat") {
                setFoodAllowance({
                    ...foodAllowance,
                    food_allowance_type: value,
                    flat_amount: "",
                    min_food_allowance_days: "",
                    variable_food_allowance_daily_rate: "",
                    variable_food_allowance_max_rate: "",
                });
            } else {
                setFoodAllowance({
                    ...foodAllowance,
                    food_allowance_type: value,
                    flat_amount: "",
                    min_food_allowance_days: "",
                    variable_food_allowance_daily_rate: "",
                    variable_food_allowance_max_rate: "",
                });
            }
            setErrors({});
        } else {
            setFoodAllowance({
                ...foodAllowance,
                [name]: value,
            });
            Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
                validate({
                    ...foodAllowance,
                    [name]: value,
                });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validate()) {
            if (editedRecord?.food_allowance_configuration_pk) {
                dispatch(UpdateFoodAllowanceAction({
                    ...foodAllowance,
                }));
            }
            setOpenPopup(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors?.food_allowance_type} size="small">
                        <InputLabel id="food_allowance-type-label">Food Allowance Type *</InputLabel>
                        <Select
                            labelId="food_allowance-type-label"
                            name="food_allowance_type"
                            value={foodAllowance?.food_allowance_type}
                            label="Food Allowance Type"
                            onChange={handleInput}
                        >
                            <MenuItem value="Flat">Flat</MenuItem>
                            <MenuItem value="Variable">Variable</MenuItem>
                        </Select>
                        <FormHelperText>{errors?.food_allowance_type}</FormHelperText>
                    </FormControl>
                </Grid>
                {foodAllowance?.food_allowance_type === "Flat" && (
                    <Grid item xs={12} md={6}>
                        <TextField
                            size="small"
                            required
                            type="number"
                            value={foodAllowance?.flat_amount}
                            onChange={handleInput}
                            variant="outlined"
                            fullWidth
                            id="flat_amount"
                            label="Amount / Month"
                            name="flat_amount"
                            autoComplete="flat_amount"
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="A fixed monthly amount pro-rated based on attendance. Paid only if minimum eligible days are met.">
                                        <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                    </Tooltip>
                                ),
                            }}
                            {...(errors.flat_amount && {
                                error: true,
                                helperText: errors.flat_amount,
                            })}
                        />
                    </Grid>
                )}
               {foodAllowance?.food_allowance_type === "Variable" && (
                    <>
                        <Grid item xs={12} md={6}>
                            <TextField
                                size="small"
                                required
                                type="number"
                                value={foodAllowance?.min_food_allowance_days}
                                onChange={handleInput}
                                variant="outlined"
                                fullWidth
                                id="min_food_allowance_days"
                                label="Minimum Eligible Days"
                                name="min_food_allowance_days"
                                autoComplete="min_food_allowance_days"
                                InputProps={{
                                    endAdornment: (
                                        <Tooltip title="Minimum number of working days required to receive any food allowance.">
                                            <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                        </Tooltip>
                                    ),
                                }}
                                {...(errors.min_food_allowance_days && {
                                    error: true,
                                    helperText: errors.min_food_allowance_days,
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                size="small"
                                required
                                type="number"
                                value={foodAllowance?.variable_food_allowance_daily_rate}
                                onChange={handleInput}
                                variant="outlined"
                                fullWidth
                                id="variable_food_allowance_daily_rate"
                                label="Per-Day Amount"
                                name="variable_food_allowance_daily_rate"
                                autoComplete="variable_food_allowance_daily_rate"
                                InputProps={{
                                    endAdornment: (
                                        <Tooltip title="Daily food allowance amount multiplied by eligible working days.">
                                            <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                        </Tooltip>
                                    ),
                                }}
                                {...(errors.variable_food_allowance_daily_rate && {
                                    error: true,
                                    helperText: errors.variable_food_allowance_daily_rate,
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                size="small"
                                required
                                type="number"
                                value={foodAllowance?.variable_food_allowance_max_rate}
                                onChange={handleInput}
                                variant="outlined"
                                fullWidth
                                id="variable_food_allowance_max_rate"
                                label="Maximum Monthly Limit"
                                name="variable_food_allowance_max_rate"
                                autoComplete="variable_food_allowance_max_rate"
                                InputProps={{
                                    endAdornment: (
                                        <Tooltip title="Maximum food allowance payable for the month, even if calculated amount exceeds it.">
                                            <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                        </Tooltip>
                                    ),
                                }}
                                {...(errors.variable_food_allowance_max_rate && {
                                    error: true,
                                    helperText: errors.variable_food_allowance_max_rate,
                                })}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
            <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                    type="submit"
                    variant="contained"
                    size="medium"
                    className="mpl-primary-btn"
                >
                    {editedRecord ? "Update" : "Save"}
                </Button>
            </Box>
        </Box>
    );
};
export default FoodAllowance;
